import { SxProps, Theme } from '@mui/material';

export const container: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  overflowX: 'hidden',
  overflowY: 'visible',
  minWidth: 0,
};

export const searchAndFilters: SxProps<Theme> = {
  mb: 3,
};

export const errorPaper: SxProps<Theme> = {
  p: 2,
  mb: 2,
};

export const tableRow: SxProps<Theme> = {
  '&:hover': {
    backgroundColor: 'action.hover',
    '& .play-button': {
      opacity: '1 !important',
    },
  },
};

export const clickableTableRow: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'action.hover',
    '& .play-button': {
      opacity: '1 !important',
    },
  },
};

export const artworkContainer: SxProps<Theme> = {
  position: 'relative',
  width: 60,
  height: 60,
  display: 'inline-block',
  cursor: 'pointer',
  '&:hover .play-button': {
    opacity: '1 !important',
  },
};

export const artworkImage: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 1,
};

export const artworkPlaceholder: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'action.disabledBackground',
  borderRadius: 1,
};

export const playButton = (isPlaying: boolean): SxProps<Theme> => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.6) !important',
  color: 'white !important',
  width: 20,
  height: 20,
  minWidth: 20,
  padding: 0,
  borderRadius: '50% !important',
  opacity: isPlaying ? 1 : 0,
  transition: 'opacity 0.2s',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8) !important',
    opacity: 1,
  },
  '& .MuiSvgIcon-root': {
    color: 'white !important',
    fontSize: '10px !important',
  },
});

export const playButtonIcon: SxProps<Theme> = {
  fontSize: 10,
};

export const collectionsContainer: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.5,
  alignItems: 'center',
};

export const collectionChip: SxProps<Theme> = {
  height: 20,
};

export const metadataChip: SxProps<Theme> = {
  height: 20,
};

export const tableContainer: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto !important',
  overflowY: 'visible !important',
  position: 'relative',
  '& .MuiTable-root': {
    minWidth: 800,
    width: 'max-content',
  },
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
  },
};

export const metadataCellContainer: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.5,
  alignItems: 'center',
  minHeight: 32,
};

export const pagination: SxProps<Theme> = {
  width: '100%',
  overflow: 'visible',
  borderTop: 1,
  borderColor: 'divider',
  display: 'flex',
  justifyContent: 'flex-end',
};

// ─── Search & layout ─────────────────────────────────────────────────────────

export const searchRow: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  mb: 2,
};

export const tableWrapper: SxProps<Theme> = {
  position: 'relative',
  overflow: 'visible',
};

export const loadingContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  pt: 6,
};

export const loadingOverlay: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
};

export const tableContainerWithLoading = (loading: boolean): SxProps<Theme> => ({
  ...tableContainer,
  opacity: loading ? 0.5 : 1,
  pointerEvents: loading ? 'none' : 'auto',
});

export const table: SxProps<Theme> = {
  tableLayout: 'auto',
  width: 'max-content',
};

export const tableHeadSticky: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: 'background.paper',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  '& .MuiTableCell-root': {
    backgroundColor: 'background.paper',
  },
};

export const emptyStateCell: SxProps<Theme> = {
  textAlign: 'center',
  py: 8,
};

export const emptyStateContent: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
};

// ─── Cells ───────────────────────────────────────────────────────────────────

export const titleCellTruncate: SxProps<Theme> = {
  maxWidth: 250,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const clickableLink: SxProps<Theme> = {
  cursor: 'pointer',
  color: 'primary.main',
  textDecoration: 'underline',
  '&:hover': { color: 'primary.dark' },
};

export const metadataChipClickable: SxProps<Theme> = {
  ...metadataChip,
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'action.hover' },
};

export const isrcText = (hasIsrc: boolean): SxProps<Theme> => ({
  color: hasIsrc ? 'text.primary' : 'text.secondary',
});

// ─── Filters ──────────────────────────────────────────────────────────────────

export const filterFieldFlex = (flex?: number): SxProps<Theme> => ({
  flex: flex ?? 1,
});

export const filtersAccordionDetails: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const filtersRow: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
};

export const activeFiltersSection: SxProps<Theme> = {
  mt: 2,
};

export const activeFiltersLabel: SxProps<Theme> = {
  mb: 1,
};

export const activeFiltersChips: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

export const clearAllFiltersButton: SxProps<Theme> = {
  mt: 1,
};

export const filtersWrapper: SxProps<Theme> = (theme) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
});

export const filtersAccordion: SxProps<Theme> = (theme) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
  '&:before': {
    backgroundColor: `${theme.palette.divider} !important`,
  },
});

export const filtersAccordionDetailsBackground: SxProps<Theme> = (theme) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
});
